import { Modal, message } from 'antd';
import { messageFor } from '../../../service/mfa.service';
import {
  LIFECYCLE_ERRORS,
  MEMBERSHIP_ERRORS,
  clearSecondFactor,
  deactivateUser,
  reactivateUser,
  sendPasswordResetEmail,
  type SecondFactor
} from '../../../service/userLifecycle.service';
import { 
  removeWorkspaceMember 
} from '../../../service/workspaceMembers.service';

/** The few fields these actions need; both table rows and drawer people satisfy it. */
export interface ActionPerson {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  enabled: boolean;
  memberships?: { node_id: number; node_name: string }[];
}

export const personName = (person: ActionPerson) =>
  [person.firstName, person.lastName].filter(Boolean).join(' ') || person.email;

/** Resolves once the confirm is answered; rejects inside onOk so the dialog shows its spinner. */
const confirm = (config: Parameters<typeof Modal.confirm>[0]) => Modal.confirm({
  centered: true,
  ...config
});

export const confirmToggleActive = (person: ActionPerson, onDone: () => void) => {
  const suspending = person.enabled;
  confirm({
    title: suspending ? `Suspend ${personName(person)}?` : `Reactivate ${personName(person)}?`,
    content: suspending
      ? `${person.email} will not be able to sign in until the account is reactivated.`
      : `${person.email} will be able to sign in again.`,
    okText: suspending ? 'Suspend account' : 'Reactivate account',
    okButtonProps: { danger: suspending },
    onOk: async () => {
      try {
        if (suspending) await deactivateUser(person.email);
        else await reactivateUser(person.email);
        message.success(suspending ? 'Account suspended.' : 'Account reactivated.');
        onDone();
      } catch (error: any) {
        message.error(messageFor(error, LIFECYCLE_ERRORS));
      }
    }
  });
};

/** Remove every in-scope membership. `scopeNodeIds` limits it to what the console manages. */
export const removeFromScope = async (
  person: ActionPerson,
  scopeNodeIds: number[]
): Promise<void> => {
  const targets = (person.memberships ?? []).filter((m) => scopeNodeIds.includes(m.node_id));
  for (const membership of targets) {
    await removeWorkspaceMember(membership.node_id, person.id);
  }
};

export const confirmRemoveFromScope = (
  person: ActionPerson,
  scopeNodeIds: number[],
  scopeLabel: string,
  onDone: () => void,
  /** Called after a failure (possibly partial), so the caller can reload; defaults to onDone. */
  onFailed?: () => void
) => {
  confirm({
    title: `Remove ${personName(person)} from ${scopeLabel}?`,
    content: 'Removes every role they hold here and below. Keeps their account.',
    okText: 'Remove',
    okButtonProps: { danger: true },
    onOk: async () => {
      try {
        await removeFromScope(person, scopeNodeIds);
        message.success(`${personName(person)} removed from ${scopeLabel}.`);
        onDone();
      } catch (error: any) {
        message.error(messageFor(error, MEMBERSHIP_ERRORS, 'The member could not be removed.'));
        (onFailed ?? onDone)();
      }
    }
  });
};

export const confirmResetFactor = (
  person: ActionPerson,
  method: SecondFactor,
  onDone: () => void
) => {
  const factor = 'Authenticator app';
  confirm({
    title: `Reset ${factor} for ${personName(person)}?`,
    content: 'They will set it up again at their next sign-in.',
    okText: 'Reset',
    okButtonProps: { danger: true },
    onOk: async () => {
      try {
        await clearSecondFactor(person.id, method);
        message.success(`${factor} reset.`);
        onDone();
      } catch (error: any) {
        message.error(messageFor(error, LIFECYCLE_ERRORS, `${factor} could not be reset.`));
      }
    }
  });
};

export const confirmSendPasswordReset = (person: ActionPerson) => {
  confirm({
    title: `Email ${person.email} a link to choose a new password?`,
    okText: 'Send email',
    onOk: async () => {
      try {
        await sendPasswordResetEmail(person.email);
        message.success(`Password reset email sent to ${person.email}.`);
      } catch (error: any) {
        message.error(messageFor(error, LIFECYCLE_ERRORS, 'The email could not be sent.'));
      }
    }
  });
};
